import { Box, Typography } from '@mui/material';
import { DeleteOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import { useTranslation } from '../../contexts/TranslationContext';
import AlertDialog from '../dialog/Alert';
import { ReactElement, RefObject, useState } from 'react';
import { MultipleRemoveResponse } from '../../api/types';
import { useSnackbar } from 'notistack';
import { QLiveTableRef } from '../qlive/types';
import { useResponseHandler } from '../../utils/response';
import { useNavigate } from 'react-router-dom';
import ResponsiveButton from '../misc/ResponsiveButton';

export type SelectionToolboxRoutes = {
    view?: string;
    edit?: string;
};

export type MiddleWareExecuteFunction = (...args: any[]) => void;

export type BatchDeleteApiCall = {
    params?: Array<any>;
    function: (
        selectedIds: Array<string>,
        ...otherParams: any[]
    ) => Promise<MultipleRemoveResponse | null>;
    middleWare?: (executeCallback: MiddleWareExecuteFunction) => void;
    afterExecution?: () => void;
};

type SelectionToolboxProps = {
    selection: Array<string>;
    selectionRef?: RefObject<QLiveTableRef | undefined>;
    apiCall?: BatchDeleteApiCall;
    routes: SelectionToolboxRoutes;
    additionalButtons?: ReactElement;
};

export default function SelectionToolbox({
    selection,
    selectionRef,
    apiCall,
    routes,
    additionalButtons,
}: SelectionToolboxProps) {
    const navigate = useNavigate();
    const { translate } = useTranslation();
    const { handleResponse } = useResponseHandler();
    const { enqueueSnackbar } = useSnackbar();

    const [deleteDialog, setDeleteDialog] = useState(false);

    const onDeleteAction = () => {
        setDeleteDialog(true);
    };

    const closeDeleteDialog = () => {
        setDeleteDialog(false);
    };

    const runDeletion = (newParams?: any[]) => {
        if (apiCall?.function) {
            closeDeleteDialog();
            const params = newParams ?? apiCall.params ?? [];
            apiCall
                .function(selection, ...params)
                .then(response => {
                    handleResponse(response, () => {
                        enqueueSnackbar(translate('messages.deleted'), {
                            variant: 'success',
                        });
                        selectionRef?.current?.reset();
                    });
                    if (apiCall.afterExecution) {
                        apiCall.afterExecution();
                    }
                })
                .catch(() => {
                    enqueueSnackbar(translate('errors.unknown'), {
                        variant: 'error',
                    });
                });
        }
    };

    const runMiddleware = () => {
        if (apiCall) {
            if (apiCall.middleWare) {
                closeDeleteDialog();
                apiCall.middleWare((...newArgs: any[]) => runDeletion(newArgs));
            } else {
                runDeletion();
            }
        }
    };

    const openView = () => {
        const id = selection.shift();
        if (id && routes.view) {
            navigate(routes.view.replace(':id', id));
        }
    };

    const openEdit = () => {
        const id = selection.shift();
        if (id && routes.edit) {
            navigate(routes.edit.replace(':id', id));
        }
    };

    return (
        <>
            {selection.length > 0 && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        borderStyle: 'solid',
                        borderWidth: 0.5,
                        borderColor: theme => theme.palette.divider,
                        px: 1,
                        borderRadius: 1,
                        minWidth: 150,
                    }}>
                    <Typography
                        variant="caption"
                        lineHeight={1}
                        sx={{ py: 1.2 }}>
                        {`${translate('form.general.selection')}: ${selection.length}`}
                    </Typography>
                    {additionalButtons}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                        }}>
                        {selection.length === 1 && (
                            <>
                                {routes.view && (
                                    <ResponsiveButton
                                        key="view"
                                        onClick={openView}
                                        icon={<EyeOutlined />}
                                        color="info">
                                        {translate('form.general.view')}
                                    </ResponsiveButton>
                                )}
                                {routes.edit && (
                                    <ResponsiveButton
                                        key="edit"
                                        onClick={openEdit}
                                        icon={<EditOutlined />}
                                        color="warning">
                                        {translate('form.general.edit')}
                                    </ResponsiveButton>
                                )}
                            </>
                        )}
                        {apiCall && (
                            <ResponsiveButton
                                key="delete"
                                icon={<DeleteOutlined />}
                                color="error"
                                onClick={onDeleteAction}>
                                {translate('form.general.delete')}
                            </ResponsiveButton>
                        )}
                    </Box>
                </Box>
            )}
            <AlertDialog
                isOpen={deleteDialog}
                title={translate('form.general.delete')}
                message={translate(
                    'form.general.batch_delete_alert',
                    selection.length,
                )}
                onCancel={closeDeleteDialog}
                onSubmit={runMiddleware}
                cancelDescription={translate('form.general.cancel')}
                submitDescription={translate('form.general.delete')}
            />
        </>
    );
}
